import { Button } from "@alm/flow-ds/components";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
	Redirect,
	Route,
	Switch,
	useHistory,
	useLocation,
	useParams,
} from "react-router-dom";
import queryString from "query-string";

import Breadcrumb from "components/Breadcrumb";
import InfoBar from "components/InfoBar";
import DashboardLayout from "components/Layouts/DashboardLayout";
import PageHeader from "components/PageHeader";
import { isAdmin, isManager } from "data/app/appSelectors";
import useCheckMountedStatus from "hooks/useCheckMountedStatus";
import usePagedData from "hooks/usePagedData";
import api from "lib/api";
import VariablesTable from "components/VariablesTable";
import ReassignDialog from "components/ReassignDialog";
import PriorityMarker from "components/Tasks/PriorityMarker";
import CustomError from "lib/api/CustomError";
import { AxiosError } from "axios";
import { useGetConvertedUserTime } from "hooks/useTimezone";
import TasksViewError from "./TasksViewError";
import { InfoBarColumn } from "components/InfoBar/InfoBar";

interface Task {
	title: string;
	status: string;
	assignee: string;
	activityInstanceId: string;
	workflow: string;
	formKey: string;
	created: string;
	endDate: string;
	dueDate: string;
	priority: string;
}

interface RouteProps {
	id: string;
	type: "admin" | "manager";
}

const TaskDetailedView = () => {
	const { type, id } = useParams<RouteProps>();

	const { fromInstance }: { fromInstance?: string } = queryString.parse(
		window.location.search
	);

	const { t } = useTranslation();

	const history = useHistory();
	const location = useLocation();

	const admin = useSelector(isAdmin);
	const manager = useSelector(isManager);

	const [task, setTask] = useState({} as Task);

	const { _isMounted } = useCheckMountedStatus();

	const [init, setInit] = useState(false);

	const [error, setError] = useState({} as CustomError);

	const { ready } = usePagedData();

	useEffect(() => {
		if (!init) {
			api.workflow
				.getTaskDetail(id)
				.then(result => {
					// Only update state when the component is mounted
					if (_isMounted.current) {
						setTask(result);
						ready();
						setInit(true);
					}
				})
				.catch(e => {
					console.error(e);
					const newError = e as AxiosError;
					const errorMessage: string | undefined =
						e.response?.data?.message;

					setError(
						new CustomError({
							httpCode: newError.response?.status,
							dump: newError,
							message:
								errorMessage !== undefined &&
								errorMessage.includes("Business Logic Error")
									? errorMessage
									: undefined,
						})
					);
				});
		}
	}, [_isMounted, init, ready, setTask, id]);

	const {
		title,
		status,
		assignee,
		activityInstanceId,
		workflow,
		formKey,
		created,
		endDate,
		dueDate,
		priority,
	} = task;

	const createdConverted = useGetConvertedUserTime(created);
	const endDateConverted = useGetConvertedUserTime(endDate);
	const dueDateConverted = useGetConvertedUserTime(dueDate);

	const crumb = useMemo(
		() =>
			Boolean(fromInstance)
				? [
						{
							name: t(
								"main.activityInstances.headings.activityInstances"
							),
							link: "/activity-instances",
						},
						{
							name: workflow || t("main.text.na"),
							link: `/activity-instance/${activityInstanceId}`,
						},
						{ name: title || t("main.text.na") },
				  ]
				: [
						{
							name: t("main.tasks.headings.tasks"),
							link:
								(location.state as { from: string })?.from ||
								`/${type}/tasks`,
						},
						{ name: title || t("main.text.na") },
				  ],
		[
			activityInstanceId,
			fromInstance,
			location.state,
			t,
			title,
			type,
			workflow,
		]
	);

	const taskData: InfoBarColumn[][] = [
		[
			{
				prop: t("main.tasks.headings.title"),
				value: title,
				width: "lg",
			},
			{
				prop: t("main.tasks.headings.taskId"),
				value: id,
				width: "lg",
			},
			{
				prop: t("main.tasks.headings.status"),
				value: status,
				width: "lg",
			},
			{
				prop: t("main.tasks.headings.assignee"),
				value: assignee,
				width: "lg",
			},
		],
		[
			{
				prop: t("main.tasks.headings.activityInstanceId"),
				value: activityInstanceId,
				width: "lg",
			},
			{
				prop: t("main.tasks.headings.workflow"),
				value: workflow,
				width: "lg",
			},
			{
				prop: t("main.tasks.headings.formKey"),
				value: formKey,
				width: "lg",
			},
			{
				prop: t("main.tasks.headings.priority"),
				value: (
					<PriorityMarker
						priority={priority as "HIGH" | "LOW" | "NORMAL"}
					/>
				),
				width: "lg",
			},
		],
		[
			{
				prop: t("main.tasks.headings.created"),
				value: createdConverted,
				width: "lg",
			},
			{
				prop: t("main.tasks.headings.endDate"),
				value: endDateConverted,
				width: "lg",
			},
			{
				prop: t("main.tasks.headings.dueDate"),
				value: dueDateConverted,
				width: "lg",
			},
			{
				prop: "",
				value: "",
				width: "lg",
			},
		],
	];

	if (Object.keys(error).length > 0)
		return (
			<DashboardLayout>
				<TasksViewError type={type} error={error} />
			</DashboardLayout>
		);

	return (
		<DashboardLayout>
			{(admin && type === "admin") || (manager && type === "manager") ? (
				<div className="container">
					<Breadcrumb crumb={crumb} />
					<PageHeader
						title={title || t("main.text.na")}
						className="page-header__tasks__detailed-view">
						{task.status === "Active" && (
							<Button
								className="btn-primary tasks--button__assign"
								label={t("main.tasks.labels.assign")}
								onClick={() =>
									history.push(`/${type}/task/${id}/assign`)
								}
							/>
						)}
					</PageHeader>
					<InfoBar data={taskData} />
					{admin && type === "admin" && (
						<VariablesTable
							getVariables={api.workflow.getTaskVariablesAsAdmin}
							entityId={id}
						/>
					)}
				</div>
			) : (
				<Redirect to="/403" />
			)}
			<Switch>
				<Route
					exact
					path={`/${type + "/task/" + id}/assign`}
					render={() => <ReassignDialog id={id} />}
				/>
			</Switch>
		</DashboardLayout>
	);
};

export default TaskDetailedView;
